import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiUrl } from "../../utils/api";
import "./CustomerConfirmPage.css";

type ConfirmDive = {
  completion_id: string;
  participant_id: string;
  session_id: string;
  status: string;
  dive_time?: string | null;
  site_name?: string | null;
  session_type?: string | null;
  guide_name?: string | null;
  location?: string | null;
};

type ConfirmPayload = {
  ok: boolean;
  status: "valid" | "already_confirmed" | "confirmed" | "expired" | "invalid" | string;
  operating_date?: string | null;
  expires_at?: string | null;
  confirmed_at?: string | null;
  consumed_at?: string | null;
  operator?: {
    operator_id?: string | null;
    name?: string | null;
    timezone?: string | null;
  };
  diver?: {
    participant_id?: string | null;
    name?: string | null;
    certification?: string | null;
  };
  dives?: ConfirmDive[];
  totals?: {
    dive_count?: number;
    completed_count?: number;
    did_not_dive_count?: number;
  };
};

type PageState =
  | { kind: "loading" }
  | { kind: "ready"; data: ConfirmPayload }
  | { kind: "confirmed"; data: ConfirmPayload }
  | { kind: "already"; data: ConfirmPayload }
  | { kind: "expired"; data: ConfirmPayload | null };

function WaveIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 11c2-2.6 4-2.6 6 0s4 2.6 6 0 4-2.6 6 0" />
      <path d="M2 16c2-2.6 4-2.6 6 0s4 2.6 6 0 4-2.6 6 0" />
    </svg>
  );
}

function ReadyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function ExpiredIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5" />
      <path d="M12 16h.01" />
    </svg>
  );
}

function initials(value?: string | null): string {
  const parts = String(value || "Diver").trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "D";
  const second = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
  return `${first}${second}`.toUpperCase();
}

function formatDate(value?: string | null): string {
  if (!value) return "today";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric"
  });
}

function operatorName(data?: ConfirmPayload | null): string {
  return data?.operator?.name || "Blue Current Divers";
}

function diverName(data?: ConfirmPayload | null): string {
  return data?.diver?.name || "Diver";
}

function diveCount(data?: ConfirmPayload | null): number {
  return Number(data?.totals?.dive_count || data?.dives?.length || 0);
}

function dayTotalLabel(data?: ConfirmPayload | null): string {
  const count = diveCount(data);
  if (count === 1) return "1 dive";
  return `${count} dives`;
}

function contactText(data?: ConfirmPayload | null): string {
  return operatorName(data);
}

function ConfirmShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="confirm-wrap">
      <div className="confirm">{children}</div>
    </div>
  );
}

function Crown({ data, title, subtitle }: { data?: ConfirmPayload | null; title?: string; subtitle?: string }) {
  return (
    <div className="cf-crown">
      <div className="cf-op">
        <span className="cf-op-mark">
          <WaveIcon />
        </span>
        <div>
          <div className="cf-op-name">{operatorName(data)}</div>
          <div className="cf-op-sub">Aqaba, Jordan</div>
        </div>
      </div>
      {title ? <h1 className="cf-title">{title}</h1> : null}
      {subtitle ? <p className="cf-title-sub">{subtitle}</p> : null}
    </div>
  );
}

function ReadyState({ data, onConfirm, isSubmitting }: { data: ConfirmPayload; onConfirm: () => void; isSubmitting: boolean }) {
  const dives = data.dives || [];

  return (
    <div className="state-panel active" id="panel-ready">
      <ConfirmShell>
        <Crown
          data={data}
          title="Confirm your dives"
          subtitle="Please review your diving and confirm the details below."
        />

        <div className="cf-body">
          <div className="cf-diver">
            <span className="cf-diver-av">{initials(diverName(data))}</span>
            <div>
              <div className="cf-diver-name">{diverName(data)}</div>
              <div className="cf-diver-cert">{data.diver?.certification || "Certification not listed"}</div>
            </div>
          </div>

          <div className="cf-sec-label">Your dives · {formatDate(data.operating_date)}</div>
          <div className="cf-dives">
            {dives.map((dive, index) => (
              <div className="cf-dive" key={`${dive.completion_id}-${index}`}>
                <span className="cf-dive-num">{index + 1}</span>
                <div className="cf-dive-main">
                  <div className="cf-dive-loc">{dive.site_name || dive.location || "Dive site"}</div>
                  <div className="cf-dive-meta">Guide: {dive.guide_name || "Your dive team"} · {dive.session_type || "Dive"}</div>
                </div>
                <div className="cf-dive-time"><strong>{dive.dive_time || "--:--"}</strong>Dive {index + 1}</div>
              </div>
            ))}
          </div>

          <div className="cf-bill">
            <div className="cf-bill-l">Total for the day<small>{dayTotalLabel(data)} · what we will settle</small></div>
            <div className="cf-bill-amt">{dayTotalLabel(data)}</div>
          </div>
        </div>

        <div className="cf-foot">
          <button className="cf-confirm" type="button" onClick={onConfirm} disabled={isSubmitting}>
            <ReadyIcon />
            Confirm my dives
          </button>
          <p className="cf-reassure">This confirms the dives listed above so your dive center can settle your booking. This is not a payment.</p>
          <p className="cf-contact">Questions? {contactText(data)}</p>
        </div>
      </ConfirmShell>
    </div>
  );
}

function ConfirmedState({ data }: { data: ConfirmPayload }) {
  return (
    <div className="state-panel active" id="panel-confirmed">
      <ConfirmShell>
        <Crown data={data} />
        <div className="cf-result confirmed">
          <div className="cf-result-ic">
            <ReadyIcon />
          </div>
          <h2 className="cf-result-title">Thank you — your dives are confirmed</h2>
          <p className="cf-result-text">{operatorName(data)} has your confirmation for all {dayTotalLabel(data)} on {formatDate(data.operating_date)}. You may close this page.</p>
          <div className="cf-result-card">
            <div className="rc-row"><span>Diver</span><strong>{diverName(data)}</strong></div>
            <div className="rc-row"><span>Dives confirmed</span><strong>{diveCount(data)}</strong></div>
            <div className="rc-row"><span>Day total</span><strong>{dayTotalLabel(data)}</strong></div>
            <div className="rc-row"><span>Confirmed</span><strong>Just now</strong></div>
          </div>
        </div>
        <div className="cf-foot">
          <p className="cf-contact">Questions? {contactText(data)}</p>
        </div>
      </ConfirmShell>
    </div>
  );
}

function AlreadyState({ data }: { data: ConfirmPayload }) {
  return (
    <div className="state-panel active" id="panel-already">
      <ConfirmShell>
        <Crown data={data} />
        <div className="cf-result confirmed">
          <div className="cf-result-ic">
            <ReadyIcon />
          </div>
          <h2 className="cf-result-title">Already confirmed</h2>
          <p className="cf-result-text">Your dives on {formatDate(data.operating_date)} were already confirmed. There is nothing more to do — you may close this page.</p>
          <div className="cf-result-card">
            <div className="rc-row"><span>Diver</span><strong>{diverName(data)}</strong></div>
            <div className="rc-row"><span>Dives confirmed</span><strong>{diveCount(data)}</strong></div>
            <div className="rc-row"><span>Day total</span><strong>{dayTotalLabel(data)}</strong></div>
          </div>
        </div>
        <div className="cf-foot">
          <p className="cf-contact">Questions? {contactText(data)}</p>
        </div>
      </ConfirmShell>
    </div>
  );
}

function ExpiredState({ data }: { data: ConfirmPayload | null }) {
  return (
    <div className="state-panel active" id="panel-expired">
      <ConfirmShell>
        <Crown data={data} />
        <div className="cf-result expired">
          <div className="cf-result-ic">
            <ExpiredIcon />
          </div>
          <h2 className="cf-result-title">This link is no longer valid</h2>
          <p className="cf-result-text">This confirmation link has expired or has already been used. Please contact your dive center and they will help you confirm your dives.</p>
        </div>
        <div className="cf-foot">
          <p className="cf-contact">Contact {contactText(data)}</p>
        </div>
      </ConfirmShell>
    </div>
  );
}

export default function CustomerConfirmPage() {
  const { token } = useParams();
  const [pageState, setPageState] = useState<PageState>({ kind: "loading" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const response = await fetch(apiUrl(`/api/v1/confirm/${encodeURIComponent(token || "")}`), {
          method: "GET",
          cache: "no-store"
        });
        const json = (await response.json().catch(() => null)) as ConfirmPayload | null;

        if (!mounted) return;

        if (!response.ok || !json || json.status === "expired" || json.status === "invalid") {
          setPageState({ kind: "expired", data: json });
          return;
        }

        if (json.status === "already_confirmed" || json.status === "confirmed" || json.consumed_at) {
          setPageState({ kind: "already", data: json });
          return;
        }

        setPageState({ kind: "ready", data: json });
      } catch {
        if (mounted) setPageState({ kind: "expired", data: null });
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [token]);

  async function confirmDives() {
    if (!token || pageState.kind !== "ready" || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(apiUrl(`/api/v1/confirm/${encodeURIComponent(token)}`), {
        method: "POST",
        cache: "no-store"
      });
      const json = (await response.json().catch(() => null)) as ConfirmPayload | null;

      if (!response.ok || !json) {
        setPageState({ kind: "expired", data: json });
        return;
      }

      if (json.status === "already_confirmed") {
        setPageState({ kind: "already", data: { ...pageState.data, ...json } });
        return;
      }

      setPageState({ kind: "confirmed", data: { ...pageState.data, ...json, status: "confirmed" } });
    } catch {
      setPageState({ kind: "expired", data: pageState.kind === "ready" ? pageState.data : null });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (pageState.kind === "loading") return null;
  if (pageState.kind === "ready") return <ReadyState data={pageState.data} onConfirm={confirmDives} isSubmitting={isSubmitting} />;
  if (pageState.kind === "confirmed") return <ConfirmedState data={pageState.data} />;
  if (pageState.kind === "already") return <AlreadyState data={pageState.data} />;
  return <ExpiredState data={pageState.data} />;
}
